import React, { Component } from 'react';
import { VehicleSpecificPricing } from './partial/VehicleSpecificPricing';
import BootstrapTable from 'react-bootstrap-table-next';
import { Modal, Button, Form, Row, Col, Table } from 'react-bootstrap';
import "react-bootstrap-table/css/react-bootstrap-table.css";

import { HTTPService } from "../services/httpService";

const API = new HTTPService();

// Fallback body styles in case API fails to load
const DEFAULT_BODY_STYLES = ['Convertible', 'Coupe', 'Crossover', 'Hatchback', 'Sedan', 'SUV', 'Truck', 'Wagon'];

export default class Vehicles extends Component {
    state = {
        // Vehicle catalog data
        vehicles: [], // Combined view of all vehicles (make/model/trim)
        groupedVehicles: [], // Grouped data for table display
        makes: [],
        models: [],
        trims: [],

        // Body styles from vehicle_size_map table
        bodyStyles: DEFAULT_BODY_STYLES,
        
        // Search/Filter
        searchTerm: '',
        selectedMakeFilter: 'All Makes',
        
        // Add Vehicle Modal - Year-based filtering
        showAddVehicle: false,
        newVehicle: {
            year: new Date().getFullYear(),
            make: '',
            makeId: '',
            model: '',
            modelId: '',
            trim: '',
            bodyStyle: ''
        },
        
        // Available options based on year
        availableMakes: [],
        availableModels: [],
        availableTrims: [],
        
        // Loading states
        loading: false,
        saving: false,
        loadingMakes: false,
        loadingModels: false,
        loadingTrims: false,
        
        // Price Override Modal (keep if still needed)
        showPriceOverride: false,
        selectedVehicleForOverride: null,
        
        // NEW: Service Override Modal
        showServiceOverrides: false,
        selectedVehicleForOverrides: null,
        overrideCounts: {}, // Cache for override counts
        
        // NEW: Trims Detail Modal
        showTrimsModal: false,
        selectedGroupForTrims: null,

        // Vehicle Edit Modal state
        editedTrims: {},
        selectedTrimIds: new Set(),
        bulkBodyStyle: '',
        editMake: '',
        editModel: '',
        savingEdits: false,
        savingMakeModel: false,
        deletingTrimId: null,
        newTrimName: '',
        newTrimBodyStyle: '',
        addingTrim: false,

        // Validation
        errors: {}
    }

    async componentDidMount() {
        await Promise.all([
            this.loadVehicleCatalog(),
            this.loadAllMakes(),
            this.loadBodyStyles()
        ]);
    }

    // =====================
    // Data Loading
    // =====================

    loadVehicleCatalog = async () => {
        this.setState({ loading: true });
        try {
            // Get all vehicles with joined data and override counts
            const { data: vehicles } = await API.get(`/vehiclescatalog/${this.props.typeId}`);
             // DEBUG: See what's actually coming from the API
        //console.log('Raw vehicle data:', vehicles);
        //console.log('Vehicles with override_count > 0:', vehicles.filter(v => v.override_count > 0);
            
            // Extract override counts from the vehicle data
            const overrideCounts = {};
            vehicles.forEach(vehicle => {
                overrideCounts[vehicle.id_car_trim] = vehicle.override_count || 0;
            });
            
            // Group the vehicles
            const groupedVehicles = this.groupVehicles(vehicles);
            
            this.setState({ 
                vehicles, 
                groupedVehicles,
                overrideCounts,
                loading: false 
            });
        } catch (error) {
            console.error("Error loading vehicle catalog:", error);
            this.setState({ loading: false });
        }
    }

    loadBodyStyles = async () => {
        try {
            const { data } = await API.get('/vehicle-size-map');
            const rows = Array.isArray(data) ? data : (data.data || []);
            if (rows.length > 0) {
                const styles = [...new Set(rows.map(r => r.body_style).filter(Boolean))].sort();
                this.setState({ bodyStyles: styles });
            }
        } catch (error) {
            console.error("Error loading body styles from vehicle_size_map:", error);
            // Falls back to DEFAULT_BODY_STYLES already in state
        }
    }

    // NEW: Group vehicles by make and model only (ignore year ranges to avoid duplicates)
    groupVehicles = (vehicles) => {
        const groups = {};
        
        vehicles.forEach(vehicle => {
            // Create a key for grouping by make and model only
            const groupKey = `${vehicle.make_name}|${vehicle.model_name}`;
            
            if (!groups[groupKey]) {
                groups[groupKey] = {
                    id: groupKey,
                    make_name: vehicle.make_name,
                    model_name: vehicle.model_name,
                    id_car_make: vehicle.id_car_make,
                    id_car_model: vehicle.id_car_model,
                    trims: [],
                    trim_count: 0,
                    total_service_overrides: 0,
                    start_years: [],
                    end_years: []
                };
            }
            
            // Add trim to the group
            groups[groupKey].trims.push({
                id_car_trim: vehicle.id_car_trim,
                trim_name: vehicle.trim_name,
                body_style: vehicle.body_style,
                id_car_type: vehicle.id_car_type,
                size_label: this.getSizeLabel(vehicle.id_car_type),
                override_count: vehicle.override_count || 0,
                start_production_year: vehicle.start_production_year,
                end_production_year: vehicle.end_production_year,
                // Keep the full vehicle data for service overrides
                fullVehicleData: vehicle
            });
            
            // Track years for the group
            groups[groupKey].start_years.push(vehicle.start_production_year);
            if (vehicle.end_production_year) {
                groups[groupKey].end_years.push(vehicle.end_production_year);
            }
            
            groups[groupKey].trim_count = groups[groupKey].trims.length;
            groups[groupKey].total_service_overrides += (vehicle.override_count || 0);
        });
        
        // Calculate year ranges for each group
        Object.values(groups).forEach(group => {
            const minStart = Math.min(...group.start_years);
            const maxEnd = group.end_years.length > 0 ? Math.max(...group.end_years) : null;
            group.year_range = `${minStart}-${maxEnd || 'Present'}`;
            group.start_production_year = minStart;
            group.end_production_year = maxEnd;
        });
        
        return Object.values(groups);
    }

    loadAllMakes = async () => {
        try {
            const { data: makes } = await API.get('/makes');
            this.setState({ makes });
        } catch (error) {
            console.error("Error loading makes:", error);
        }
    }

    // =====================
    // Year-based filtering for Add Vehicle
    // =====================

    loadMakesForYear = async (year) => {
        this.setState({ loadingMakes: true });
        try {
            // Get makes available for the selected year
            const { data: makes } = await API.get(`/makes/${year}`);
            this.setState({ 
                availableMakes: makes,
                availableModels: [],
                availableTrims: [],
                loadingMakes: false 
            });
            
            // Reset dependent selections
            this.handleNewVehicleChange('make', '');
            this.handleNewVehicleChange('model', '');
            this.handleNewVehicleChange('trim', '');
        } catch (error) {
            console.error("Error loading makes for year:", error);
            this.setState({ loadingMakes: false });
        }
    }

// Enhanced loadModelsForMakeYear with debugging
loadModelsForMakeYear = async (makeId, year) => {
    this.setState({ loadingModels: true });
    try {
        //console.log('=== LOADING MODELS DEBUG ===');
        //console.log('makeId:', makeId);
        //console.log('year:', year);
        
        const url = `/models/${year}${makeId.toString().padStart(4, '0')}`;
        //console.log('API URL:', url);
        
        // Get models available for this make in the selected year
        const response = await API.get(url);
        const models = response.data;
        
        //console.log('✅ Received models from server:');
        //console.log('Number of models:', models.length);
        
        /* Log each model received
        models.forEach((model, index) => {
            console.log(`Model ${index + 1}:`
                 , 
            {
                id_car_model: model.id_car_model,
                name: model.name,
                id_car_type: model.id_car_type,
                id_car_type_type: typeof model.id_car_type
            });
            
            // Special attention to 8C Competizione
            if (model.name.includes('8C') || model.name.includes('Competizione')) {
                //console.log('🔍 8C Competizione details:', model);
            }
        }); */
        
        this.setState({ 
            availableModels: models,
            availableTrims: [],
            loadingModels: false 
        });
        
        // Reset dependent selections
        this.handleNewVehicleChange('model', '');
        this.handleNewVehicleChange('trim', '');
        
    } catch (error) {
        console.error("❌ Error loading models:", error);
        console.error("Error response data:", error.response?.data);
        this.setState({ loadingModels: false });
    }
}
    loadTrimsForModel = async (modelId) => {
        this.setState({ loadingTrims: true });
        try {
            // Get existing trims for this model
            const { data: trims } = await API.get(`/trims/${modelId}`);
            this.setState({ 
                availableTrims: trims,
                loadingTrims: false 
            });
        } catch (error) {
            console.error("Error loading trims:", error);
            this.setState({ loadingTrims: false });
        }
    }

    // =====================
    // Add Vehicle Logic
    // =====================

// Enhanced debug version of addVehicle method in React component
addVehicle = async () => {
    if (!this.validateVehicle()) return;

    this.setState({ saving: true });
    try {
        const { year, make, model, trim, bodyStyle } = this.state.newVehicle;

        // Single API call that handles all the complexity
        // Size is determined server-side from body style mapping
        await API.post('/vehiclesadd', {
            year: parseInt(year),
            make: make.trim(),
            model: model.trim(),
            trim: trim.trim(),
            bodyStyle,
            usa: 1
        });

        // Reset form and refresh data
        this.setState({
            showAddVehicle: false,
            newVehicle: {
                year: new Date().getFullYear(),
                make: '',
                makeId: '',
                model: '',
                modelId: '',
                trim: '',
                bodyStyle: ''
            },
            errors: {},
            saving: false,
            availableMakes: [],
            availableModels: [],
            availableTrims: []
        });

        await this.loadVehicleCatalog();
        await this.loadAllMakes();
        
    } catch (error) {
        console.error("Error adding vehicle:", error);
        this.setState({ 
            saving: false,
            errors: { general: "Failed to add vehicle. Please try again." }
        });
    }
}
// Also enhance the validateVehicle method for debugging
validateVehicle = () => {
    const { year, make, model, trim, bodyStyle } = this.state.newVehicle;
    const errors = {};

    if (!year || year < 1900 || year > new Date().getFullYear() + 2) {
        errors.year = "Please enter a valid year";
    }
    if (!make.trim()) {
        errors.make = "Make is required";
    }
    if (!model.trim()) {
        errors.model = "Model is required";
    }
    if (!trim.trim()) {
        errors.trim = "Trim is required";
    }
    if (!bodyStyle) {
        errors.bodyStyle = "Body style is required";
    }

    this.setState({ errors });
    return Object.keys(errors).length === 0;
}
    // =====================
    // Event Handlers
    // =====================

// Clean handleTypeaheadChange method - just the fix
handleTypeaheadChange = async (field, value) => {
    // Handle make/model typeahead inputs
    this.setState({
        newVehicle: {
            ...this.state.newVehicle,
            [field]: value
        },
        errors: {
            ...this.state.errors,
            [field]: ''
        }
    });

    if (field === 'make') {
        // Check if this is an existing make
        const existingMake = this.state.availableMakes.find(make => 
            make.name.toLowerCase() === value.toLowerCase()
        );
        
        if (existingMake) {
            // Existing make - load its models
            this.setState({
                newVehicle: {
                    ...this.state.newVehicle,
                    make: value,
                    makeId: existingMake.id_car_make,
                    model: '', // Reset dependent fields
                    trim: ''
                }
            });
            await this.loadModelsForMakeYear(existingMake.id_car_make, this.state.newVehicle.year);
        } else {
            // New make - clear dependent data
            this.setState({
                newVehicle: {
                    ...this.state.newVehicle,
                    make: value,
                    makeId: '',
                    model: '',
                    trim: ''
                },
                availableModels: [],
                availableTrims: []
            });
        }
    } else if (field === 'model') {
        // Check if this is an existing model
        const existingModel = this.state.availableModels.find(model => 
            model.name.toLowerCase() === value.toLowerCase()
        );
        
        if (existingModel) {
            // Existing model - load its trims
            this.setState({
                newVehicle: {
                    ...this.state.newVehicle,
                    model: value,
                    modelId: existingModel.id_car_model,
                    trim: ''
                }
            });
            await this.loadTrimsForModel(existingModel.id_car_model);
        } else {
            // New model - clear trims
            this.setState({
                newVehicle: {
                    ...this.state.newVehicle,
                    model: value,
                    modelId: '',
                    trim: ''
                },
                availableTrims: []
            });
        }
    }
}

    handleNewVehicleChange = async (field, value) => {
        this.setState({
            newVehicle: {
                ...this.state.newVehicle,
                [field]: value
            },
            errors: {
                ...this.state.errors,
                [field]: '' // Clear error when user starts typing
            }
        });

        // Handle year change
        if (field === 'year') {
            await this.loadMakesForYear(value);
        }
    }

    handleSearch = (event) => {
        this.setState({ searchTerm: event.target.value }, () => {
            // Re-group vehicles after search change
            const filteredVehicles = this.getFilteredVehicles();
            const groupedVehicles = this.groupVehicles(filteredVehicles);
            this.setState({ groupedVehicles });
        });
    }

    handleMakeFilter = (event) => {
        this.setState({ selectedMakeFilter: event.target.value }, () => {
            // Re-group vehicles after filter change
            const filteredVehicles = this.getFilteredVehicles();
            const groupedVehicles = this.groupVehicles(filteredVehicles);
            this.setState({ groupedVehicles });
        });
    }

    // =====================
    // Modal Methods
    // =====================

    openAddVehicleModal = async () => {
        this.setState({ showAddVehicle: true });
        // Load makes for current year when modal opens
        await this.loadMakesForYear(this.state.newVehicle.year);
    }

    closeAddVehicleModal = () => {
        this.setState({ 
            showAddVehicle: false,
            availableMakes: [],
            availableModels: [],
            availableTrims: [],
            newVehicle: {
                year: new Date().getFullYear(),
                make: '',
                makeId: '',
                model: '',
                modelId: '',
                trim: '',
                bodyStyle: ''
            },
            errors: {}
        });
    }

    // =====================
    // Price Override Methods (keep if still needed)
    // =====================

    openPriceOverrideModal = (vehicle) => {
        this.setState({
            showPriceOverride: true,
            selectedVehicleForOverride: vehicle
        });
    }

    closePriceOverrideModal = () => {
        this.setState({
            showPriceOverride: false,
            selectedVehicleForOverride: null
        });
    }

    handlePriceOverrideUpdated = async () => {
        // Reload the vehicle catalog to get updated prices
        await this.loadVehicleCatalog();
        this.closePriceOverrideModal();
    }

    // =====================
    // NEW: Service Override Methods
    // =====================

    openServiceOverridesModal = (vehicle) => {
        // Format vehicle data for VehicleSpecificPricing component
        const formattedVehicle = {
            make: vehicle.make_name,
            model: vehicle.model_name,
            trimId: vehicle.id_car_trim,
            trimName: vehicle.trim_name,
            vehicleSize: vehicle.id_car_type, // S, M, or L
            bodyStyle: vehicle.body_style
        };

        this.setState({
            showServiceOverrides: true,
            selectedVehicleForOverrides: formattedVehicle
        });
    }

    closeServiceOverridesModal = () => {
        this.setState({
            showServiceOverrides: false,
            selectedVehicleForOverrides: null
        });
    }

    handleServiceOverridesUpdated = async () => {
        // Reload the vehicle catalog (which includes override counts)
        // but DON'T close the modal - let user close it manually
        await this.loadVehicleCatalog();
    }

    // =====================
    // Vehicle Edit Modal Methods
    // =====================

    openVehicleEditModal = (group) => {
        this.setState({
            showTrimsModal: true,
            selectedGroupForTrims: group,
            editMake: group.make_name,
            editModel: group.model_name,
            editedTrims: {},
            selectedTrimIds: new Set(),
            bulkBodyStyle: '',
            savingEdits: false,
            savingMakeModel: false,
            deletingTrimId: null,
            newTrimName: '',
            newTrimBodyStyle: '',
            addingTrim: false
        });
    }

    closeVehicleEditModal = () => {
        const { editedTrims } = this.state;
        if (Object.keys(editedTrims).length > 0) {
            if (!window.confirm('You have unsaved changes. Close anyway?')) return;
        }
        this.setState({
            showTrimsModal: false,
            selectedGroupForTrims: null,
            editedTrims: {},
            selectedTrimIds: new Set(),
            bulkBodyStyle: '',
            editMake: '',
            editModel: '',
            newTrimName: '',
            newTrimBodyStyle: ''
        });
    }

    handleTrimFieldChange = (trimId, field, value, originalTrim) => {
        this.setState(prevState => {
            const newEdited = { ...prevState.editedTrims };
            const trimEdits = { ...(newEdited[trimId] || {}) };
            trimEdits[field] = value;

            // Remove from edits if value matches original
            if (trimEdits.body_style === originalTrim.body_style || trimEdits.body_style === undefined) {
                delete trimEdits.body_style;
            }

            if (Object.keys(trimEdits).length === 0) {
                delete newEdited[trimId];
            } else {
                newEdited[trimId] = trimEdits;
            }

            return { editedTrims: newEdited };
        });
    }

    handleToggleTrimSelect = (trimId) => {
        this.setState(prevState => {
            const newSelected = new Set(prevState.selectedTrimIds);
            if (newSelected.has(trimId)) {
                newSelected.delete(trimId);
            } else {
                newSelected.add(trimId);
            }
            return { selectedTrimIds: newSelected };
        });
    }

    handleSelectAllTrims = () => {
        const { selectedGroupForTrims, selectedTrimIds } = this.state;
        if (!selectedGroupForTrims) return;

        const allIds = selectedGroupForTrims.trims.map(t => t.id_car_trim);
        const allSelected = allIds.length > 0 && allIds.every(id => selectedTrimIds.has(id));

        if (allSelected) {
            this.setState({ selectedTrimIds: new Set() });
        } else {
            this.setState({ selectedTrimIds: new Set(allIds) });
        }
    }

    handleBulkApply = () => {
        const { selectedTrimIds, bulkBodyStyle, editedTrims, selectedGroupForTrims } = this.state;
        if (selectedTrimIds.size === 0 || !bulkBodyStyle) return;

        const newEdited = { ...editedTrims };

        selectedGroupForTrims.trims.forEach(trim => {
            if (selectedTrimIds.has(trim.id_car_trim)) {
                if (bulkBodyStyle === trim.body_style) {
                    // Matches original — remove edit if no other changes
                    const trimEdits = { ...(newEdited[trim.id_car_trim] || {}) };
                    delete trimEdits.body_style;
                    if (Object.keys(trimEdits).length === 0) {
                        delete newEdited[trim.id_car_trim];
                    } else {
                        newEdited[trim.id_car_trim] = trimEdits;
                    }
                } else {
                    newEdited[trim.id_car_trim] = {
                        ...(newEdited[trim.id_car_trim] || {}),
                        body_style: bulkBodyStyle
                    };
                }
            }
        });

        this.setState({ editedTrims: newEdited });
    }

    handleSaveTrims = async () => {
        const { editedTrims, selectedGroupForTrims } = this.state;
        if (Object.keys(editedTrims).length === 0) return;

        this.setState({ savingEdits: true });
        try {
            const promises = Object.entries(editedTrims).map(([trimId, changes]) =>
                API.patch(`/trims/${trimId}`, changes)
            );
            await Promise.all(promises);

            // Reload catalog and refresh modal
            const { data: vehicles } = await API.get(`/vehiclescatalog/${this.props.typeId}`);
            const overrideCounts = {};
            vehicles.forEach(v => { overrideCounts[v.id_car_trim] = v.override_count || 0; });
            const groupedVehicles = this.groupVehicles(vehicles);

            const groupKey = `${selectedGroupForTrims.make_name}|${selectedGroupForTrims.model_name}`;
            const updatedGroup = groupedVehicles.find(g => g.id === groupKey);

            this.setState({
                vehicles,
                groupedVehicles,
                overrideCounts,
                editedTrims: {},
                selectedTrimIds: new Set(),
                savingEdits: false,
                selectedGroupForTrims: updatedGroup || null,
                showTrimsModal: !!updatedGroup
            });
        } catch (error) {
            console.error('Error saving trim edits:', error);
            this.setState({ savingEdits: false });
            alert('Failed to save changes. Please try again.');
        }
    }

    handleDeleteTrim = async (trimId, trimName) => {
        if (!window.confirm(`Delete trim "${trimName}"? This cannot be undone.`)) return;

        this.setState({ deletingTrimId: trimId });
        try {
            await API.delete(`/trims/${trimId}`);

            // Reload catalog and refresh modal
            const { data: vehicles } = await API.get(`/vehiclescatalog/${this.props.typeId}`);
            const overrideCounts = {};
            vehicles.forEach(v => { overrideCounts[v.id_car_trim] = v.override_count || 0; });
            const groupedVehicles = this.groupVehicles(vehicles);

            const groupKey = `${this.state.selectedGroupForTrims.make_name}|${this.state.selectedGroupForTrims.model_name}`;
            const updatedGroup = groupedVehicles.find(g => g.id === groupKey);

            // Clean up edits/selections for deleted trim
            const newEdited = { ...this.state.editedTrims };
            delete newEdited[trimId];
            const newSelected = new Set(this.state.selectedTrimIds);
            newSelected.delete(trimId);

            this.setState({
                vehicles,
                groupedVehicles,
                overrideCounts,
                editedTrims: newEdited,
                selectedTrimIds: newSelected,
                deletingTrimId: null,
                selectedGroupForTrims: updatedGroup || null,
                showTrimsModal: !!updatedGroup
            });
        } catch (error) {
            console.error('Error deleting trim:', error);
            this.setState({ deletingTrimId: null });
            alert('Failed to delete trim. Please try again.');
        }
    }

    handleSaveMakeModel = async () => {
        const { editMake, editModel, selectedGroupForTrims } = this.state;
        const makeChanged = editMake.trim() !== selectedGroupForTrims.make_name;
        const modelChanged = editModel.trim() !== selectedGroupForTrims.model_name;

        if (!makeChanged && !modelChanged) return;
        if (!editMake.trim() || !editModel.trim()) {
            alert('Make and model names cannot be empty.');
            return;
        }

        this.setState({ savingMakeModel: true });
        try {
            const promises = [];
            if (makeChanged && selectedGroupForTrims.id_car_make) {
                promises.push(API.patch(`/makes/${selectedGroupForTrims.id_car_make}`, { name: editMake.trim() }));
            }
            if (modelChanged && selectedGroupForTrims.id_car_model) {
                promises.push(API.patch(`/models/${selectedGroupForTrims.id_car_model}`, { name: editModel.trim() }));
            }
            await Promise.all(promises);

            // Reload catalog and refresh modal with new names
            const { data: vehicles } = await API.get(`/vehiclescatalog/${this.props.typeId}`);
            const overrideCounts = {};
            vehicles.forEach(v => { overrideCounts[v.id_car_trim] = v.override_count || 0; });
            const groupedVehicles = this.groupVehicles(vehicles);

            const newGroupKey = `${editMake.trim()}|${editModel.trim()}`;
            const updatedGroup = groupedVehicles.find(g => g.id === newGroupKey);

            this.setState({
                vehicles,
                groupedVehicles,
                overrideCounts,
                savingMakeModel: false,
                selectedGroupForTrims: updatedGroup || null,
                showTrimsModal: !!updatedGroup,
                editMake: updatedGroup ? updatedGroup.make_name : '',
                editModel: updatedGroup ? updatedGroup.model_name : ''
            });
            await this.loadAllMakes();
        } catch (error) {
            console.error('Error saving make/model:', error);
            this.setState({ savingMakeModel: false });
            alert('Failed to save make/model changes. Please try again.');
        }
    }

    handleAddTrim = async () => {
        const { newTrimName, newTrimBodyStyle, selectedGroupForTrims } = this.state;
        if (!newTrimName.trim()) return;

        // Check for duplicate trim name in current group
        const isDuplicate = selectedGroupForTrims.trims.some(
            t => t.trim_name.toLowerCase() === newTrimName.trim().toLowerCase()
        );
        if (isDuplicate) {
            alert(`Trim "${newTrimName.trim()}" already exists for this model.`);
            return;
        }

        this.setState({ addingTrim: true });
        try {
            await API.post('/trims', {
                id_car_model: selectedGroupForTrims.id_car_model,
                name: newTrimName.trim(),
                body_style: newTrimBodyStyle,
                start_production_year: new Date().getFullYear()
            });

            // Reload catalog and refresh modal
            const { data: vehicles } = await API.get(`/vehiclescatalog/${this.props.typeId}`);
            const overrideCounts = {};
            vehicles.forEach(v => { overrideCounts[v.id_car_trim] = v.override_count || 0; });
            const groupedVehicles = this.groupVehicles(vehicles);

            const groupKey = `${selectedGroupForTrims.make_name}|${selectedGroupForTrims.model_name}`;
            const updatedGroup = groupedVehicles.find(g => g.id === groupKey);

            this.setState({
                vehicles,
                groupedVehicles,
                overrideCounts,
                addingTrim: false,
                newTrimName: '',
                newTrimBodyStyle: '',
                selectedGroupForTrims: updatedGroup || null,
                showTrimsModal: !!updatedGroup
            });
        } catch (error) {
            console.error('Error adding trim:', error);
            this.setState({ addingTrim: false });
            alert('Failed to add trim. Please try again.');
        }
    }

    // =====================
    // Helper Methods
    // =====================

    getFilteredVehicles = () => {
        let filtered = this.state.vehicles;

        // Filter by search term
        if (this.state.searchTerm) {
            const term = this.state.searchTerm.toLowerCase();
            filtered = filtered.filter(vehicle =>
                vehicle.make_name.toLowerCase().includes(term) ||
                vehicle.model_name.toLowerCase().includes(term) ||
                vehicle.trim_name.toLowerCase().includes(term)
            );
        }

        // Filter by make
        if (this.state.selectedMakeFilter !== 'All Makes') {
            filtered = filtered.filter(vehicle => 
                vehicle.make_name === this.state.selectedMakeFilter
            );
        }

        return filtered;
    }

    getSizeLabel = (size) => {
        const sizeMap = { S: 'Small', M: 'Medium', L: 'Large', XL: 'X-Large' };
        return sizeMap[size] || size;
    }

    // =====================
    // Render Methods
    // =====================

    renderVehicleEditModal = () => {
        const { showTrimsModal, selectedGroupForTrims, editedTrims, selectedTrimIds,
                bulkBodyStyle, editMake, editModel,
                savingEdits, savingMakeModel, deletingTrimId,
                newTrimName, newTrimBodyStyle, addingTrim } = this.state;

        if (!selectedGroupForTrims) return null;

        const userRole = this.props.userRole;
        const isVehicleAdmin = userRole === 'admin' || this.props.access === 'ADMIN';
        const hasDirtyTrims = Object.keys(editedTrims).length > 0;
        const makeModelDirty = editMake.trim() !== selectedGroupForTrims.make_name
                            || editModel.trim() !== selectedGroupForTrims.model_name;
        const allTrimIds = selectedGroupForTrims.trims.map(t => t.id_car_trim);
        const allSelected = allTrimIds.length > 0 && allTrimIds.every(id => selectedTrimIds.has(id));

        return (
            <Modal
                show={showTrimsModal}
                onHide={this.closeVehicleEditModal}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isVehicleAdmin ? 'Edit: ' : ''}{selectedGroupForTrims.make_name} {selectedGroupForTrims.model_name}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {/* Zone 1: Make/Model Edit (admin only) */}
                    {isVehicleAdmin && (
                        <div className="mb-4 pb-3 border-bottom">
                            <Row>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label><strong>Make</strong></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={editMake}
                                            onChange={(e) => this.setState({ editMake: e.target.value })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label><strong>Model</strong></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={editModel}
                                            onChange={(e) => this.setState({ editModel: e.target.value })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4} className="d-flex align-items-end">
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        className="mb-3"
                                        onClick={this.handleSaveMakeModel}
                                        disabled={!makeModelDirty || savingMakeModel}
                                    >
                                        {savingMakeModel ? (
                                            <i className="fa-sharp fa-regular fa-spinner fa-spin"></i>
                                        ) : 'SAVE'}
                                    </Button>
                                </Col>
                            </Row>
                            <small className="text-muted">Vehicle size is determined automatically from body style</small>
                        </div>
                    )}

                    {/* Zone 2: Bulk Edit Bar (admin only, 2+ trims) */}
                    {isVehicleAdmin && selectedGroupForTrims.trims.length > 1 && (
                        <div className="mb-3 p-2 bg-light rounded">
                            <Row className="align-items-center">
                                <Col xs="auto">
                                    <small><strong>Bulk:</strong></small>
                                </Col>
                                <Col md={4}>
                                    <Form.Control
                                        as="select"
                                        size="sm"
                                        value={bulkBodyStyle}
                                        onChange={(e) => this.setState({ bulkBodyStyle: e.target.value })}
                                    >
                                        <option value="">Body Style...</option>
                                        {this.state.bodyStyles.map(bs => (
                                            <option key={bs} value={bs}>{bs}</option>
                                        ))}
                                    </Form.Control>
                                </Col>
                                <Col xs="auto">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={this.handleBulkApply}
                                        disabled={selectedTrimIds.size === 0 || !bulkBodyStyle}
                                    >
                                        APPLY TO SELECTED
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {/* Trim Table */}
                    <Table striped hover responsive size="sm">
                        <thead>
                            <tr>
                                {isVehicleAdmin && (
                                    <th style={{ width: '40px' }}>
                                        <Form.Check
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={this.handleSelectAllTrims}
                                        />
                                    </th>
                                )}
                                <th>Trim</th>
                                <th>Body Style</th>
                                {!isVehicleAdmin && <th>Size</th>}
                                {isVehicleAdmin && <th style={{ width: '50px' }}></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {selectedGroupForTrims.trims.map(trim => {
                                const edited = editedTrims[trim.id_car_trim] || {};
                                const currentBodyStyle = edited.body_style !== undefined ? edited.body_style : trim.body_style;
                                const isDirty = editedTrims[trim.id_car_trim] !== undefined;

                                return (
                                    <tr key={trim.id_car_trim} style={isDirty ? { backgroundColor: '#fff3cd' } : {}}>
                                        {isVehicleAdmin && (
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={selectedTrimIds.has(trim.id_car_trim)}
                                                    onChange={() => this.handleToggleTrimSelect(trim.id_car_trim)}
                                                />
                                            </td>
                                        )}
                                        <td><strong>{trim.trim_name}</strong></td>
                                        <td>
                                            {isVehicleAdmin ? (
                                                <Form.Control
                                                    as="select"
                                                    size="sm"
                                                    value={currentBodyStyle}
                                                    onChange={(e) => this.handleTrimFieldChange(
                                                        trim.id_car_trim, 'body_style', e.target.value, trim
                                                    )}
                                                >
                                                    {this.state.bodyStyles.map(bs => (
                                                        <option key={bs} value={bs}>{bs}</option>
                                                    ))}
                                                </Form.Control>
                                            ) : trim.body_style}
                                        </td>
                                        {!isVehicleAdmin && <td>{trim.size_label}</td>}
                                        {isVehicleAdmin && (
                                            <td className="text-center">
                                                <Button
                                                    variant="link"
                                                    className="text-danger p-0"
                                                    onClick={() => this.handleDeleteTrim(trim.id_car_trim, trim.trim_name)}
                                                    disabled={deletingTrimId === trim.id_car_trim}
                                                    title="Delete trim"
                                                >
                                                    {deletingTrimId === trim.id_car_trim
                                                        ? <i className="fa-sharp fa-regular fa-spinner fa-spin"></i>
                                                        : <i className="fa-sharp fa-regular fa-trash"></i>}
                                                </Button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                            {/* Add Trim row (admin only) */}
                            {isVehicleAdmin && (
                                <tr style={{ backgroundColor: '#f0f9ff' }}>
                                    <td></td>
                                    <td>
                                        <Form.Control
                                            type="text"
                                            size="sm"
                                            placeholder="New trim name..."
                                            value={newTrimName}
                                            onChange={(e) => this.setState({ newTrimName: e.target.value })}
                                            onKeyDown={(e) => { if (e.key === 'Enter') this.handleAddTrim(); }}
                                            disabled={addingTrim}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            as="select"
                                            size="sm"
                                            value={newTrimBodyStyle}
                                            onChange={(e) => this.setState({ newTrimBodyStyle: e.target.value })}
                                            disabled={addingTrim}
                                        >
                                            <option value="">Body Style...</option>
                                            {this.state.bodyStyles.map(bs => (
                                                <option key={bs} value={bs}>{bs}</option>
                                            ))}
                                        </Form.Control>
                                    </td>
                                    <td className="text-center">
                                        <Button
                                            variant="link"
                                            className="text-success p-0"
                                            onClick={this.handleAddTrim}
                                            disabled={addingTrim || !newTrimName.trim() || !newTrimBodyStyle}
                                            title="Add trim"
                                        >
                                            {addingTrim
                                                ? <i className="fa-sharp fa-regular fa-spinner fa-spin"></i>
                                                : <i className="fa-sharp fa-regular fa-plus"></i>}
                                        </Button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Modal.Body>

                <Modal.Footer>
                    {isVehicleAdmin && hasDirtyTrims && (
                        <Button
                            variant="primary"
                            onClick={this.handleSaveTrims}
                            disabled={savingEdits}
                        >
                            {savingEdits ? (
                                <>
                                    <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>
                                    SAVING...
                                </>
                            ) : `SAVE CHANGES (${Object.keys(editedTrims).length})`}
                        </Button>
                    )}
                    <Button variant="secondary" onClick={this.closeVehicleEditModal}>
                        CLOSE
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    renderAddVehicleModal = () => {
        const { showAddVehicle, newVehicle, saving, errors, loadingMakes, loadingModels, loadingTrims } = this.state;
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear + 2; year >= 1950; year--) {
            years.push(year);
        }

        // Check if selected trim already exists
        const trimExists = this.state.availableTrims.some(trim => 
            trim.name.toLowerCase() === newVehicle.trim.toLowerCase()
        );

        // Check if make/model are new
        const isNewMake = newVehicle.make && !this.state.availableMakes.some(make => 
            make.name.toLowerCase() === newVehicle.make.toLowerCase()
        );
        const isNewModel = newVehicle.model && !this.state.availableModels.some(model => 
            model.name.toLowerCase() === newVehicle.model.toLowerCase()
        );

        return (
            <Modal 
                show={showAddVehicle} 
                onHide={this.closeAddVehicleModal}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Add New Vehicle</Modal.Title>
                </Modal.Header>
                
                <Modal.Body>
                    {errors.general && (
                        <div className="alert alert-danger">{errors.general}</div>
                    )}
                    
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Year</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={newVehicle.year}
                                        onChange={(e) => this.handleNewVehicleChange('year', e.target.value)}
                                        isInvalid={!!errors.year}
                                    >
                                        {years.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.year}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>
                                        Make 
                                        {isNewMake && <small className="text-success ml-2">(New Make)</small>}
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Type to search or add new make..."
                                        value={newVehicle.make}
                                        onChange={(e) => this.handleTypeaheadChange('make', e.target.value)}
                                        onFocus={() => this.setState({ showMakeSuggestions: true })}
                                        onBlur={() => setTimeout(() => this.setState({ showMakeSuggestions: false }), 200)}
                                        isInvalid={!!errors.make}
                                        disabled={loadingMakes}
                                        list="make-suggestions"
                                    />
                                    <datalist id="make-suggestions">
                                        {this.state.availableMakes.map(make => (
                                            <option key={make.id_car_make} value={make.name} />
                                        ))}
                                    </datalist>
                                    {loadingMakes && <small className="text-muted">Loading makes...</small>}
                                    <Form.Control.Feedback type="invalid">
                                        {errors.make}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>
                                        Model
                                        {isNewModel && <small className="text-success ml-2">(New Model)</small>}
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Type to search or add new model..."
                                        value={newVehicle.model}
                                        onChange={(e) => this.handleTypeaheadChange('model', e.target.value)}
                                        onFocus={() => this.setState({ showModelSuggestions: true })}
                                        onBlur={() => setTimeout(() => this.setState({ showModelSuggestions: false }), 200)}
                                        isInvalid={!!errors.model}
                                        disabled={!newVehicle.make || loadingModels}
                                        list="model-suggestions"
                                    />
                                    <datalist id="model-suggestions">
                                        {this.state.availableModels.map(model => (
                                            <option key={model.id_car_model} value={model.name} />
                                        ))}
                                    </datalist>
                                    {loadingModels && <small className="text-muted">Loading models...</small>}
                                    <Form.Control.Feedback type="invalid">
                                        {errors.model}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Trim</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter trim name"
                                        value={newVehicle.trim}
                                        onChange={(e) => this.handleNewVehicleChange('trim', e.target.value)}
                                        isInvalid={!!errors.trim}
                                        disabled={!newVehicle.model}
                                        list="trim-suggestions"
                                    />
                                    <datalist id="trim-suggestions">
                                        {this.state.availableTrims.map(trim => (
                                            <option key={trim.id_car_trim} value={trim.name} />
                                        ))}
                                    </datalist>
                                    {trimExists && (
                                        <small className="text-warning">
                                            <i className="fa-sharp fa-regular fa-exclamation-triangle"></i> This trim already exists
                                        </small>
                                    )}
                                    {loadingTrims && <small className="text-muted">Loading existing trims...</small>}
                                    <Form.Control.Feedback type="invalid">
                                        {errors.trim}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Body Style <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={newVehicle.bodyStyle}
                                        onChange={(e) => this.handleNewVehicleChange('bodyStyle', e.target.value)}
                                        isInvalid={!!errors.bodyStyle}
                                    >
                                        <option value="">Select Body Style...</option>
                                        {this.state.bodyStyles.map(bs => (
                                            <option key={bs} value={bs}>{bs}</option>
                                        ))}
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.bodyStyle}
                                    </Form.Control.Feedback>
                                    <small className="text-muted">Vehicle size is determined automatically from body style</small>
                                </Form.Group>
                            </Col>
                        </Row>

                    </Form>
                </Modal.Body>
                
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={this.closeAddVehicleModal}
                        disabled={saving}
                    >
                        CANCEL
                    </Button>
                    <Button
                        variant="primary"
                        onClick={this.addVehicle}
                        disabled={saving || (trimExists && !isNewModel)}
                    >
                        {saving ? (
                            <>
                                <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>
                                ADDING...
                            </>
                        ) : (
                            trimExists && !isNewModel ? 'TRIM ALREADY EXISTS' : 'ADD VEHICLE'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        const {
            loading,
            groupedVehicles,
            showServiceOverrides,
            selectedVehicleForOverrides
        } = this.state;

        const userRole = this.props.userRole;
        const isAdmin = userRole === 'admin' || userRole === 'owner' || this.props.access === 'ADMIN';
        const canEditPricing = isAdmin || userRole === 'manager';

        // Prepare make filter options from original vehicles data
        const uniqueMakes = [...new Set(this.state.vehicles.map(v => v.make_name))].sort();
        const makeOptions = ['All Makes', ...uniqueMakes];

        // Updated table columns for grouped view
        const columns = [{
            dataField: 'year_range',
            text: 'Years',
            sort: true,
            headerStyle: { width: '120px' }
        }, {
            dataField: 'make_name',
            text: 'Make',
            sort: true,
            headerStyle: { width: '120px' }
        }, {
            dataField: 'model_name', 
            text: 'Model',
            sort: true,
            headerStyle: { width: '150px' }
        }, {
            dataField: 'trim_count',
            text: 'Trims',
            formatter: (cell, row) => (
                <button
                    className="btn btn-link text-primary p-0"
                    onClick={() => this.openVehicleEditModal(row)}
                    title={`Click to view ${cell} trim${cell !== 1 ? 's' : ''}`}
                    style={{ textDecoration: 'underline', fontSize: '14px' }}
                >
                    {cell} trim{cell !== 1 ? 's' : ''}
                </button>
            ),
            sort: true,
            headerStyle: { width: '120px' },
            style: { textAlign: 'center' }
        }, {
            dataField: 'service_overrides',
            text: 'Custom Pricing',
            formatter: (cell, row) => {
                const totalOverrides = row.total_service_overrides;

                if (!canEditPricing) {
                    // Read-only view for manager/viewer
                    if (totalOverrides === 0) {
                        return <span className="text-muted">—</span>;
                    }
                    return (
                        <span className="badge badge-info">
                            {totalOverrides} custom price{totalOverrides !== 1 ? 's' : ''}
                        </span>
                    );
                }

                if (totalOverrides === 0) {
                    return (
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => this.openServiceOverridesModal(row.trims[0].fullVehicleData)}
                            title="No custom pricing - click to add"
                        >
                            <i className="fa-sharp fa-regular fa-plus mr-1"></i>
                            ADD PRICING
                        </button>
                    );
                }

                return (
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => this.openServiceOverridesModal(row.trims[0].fullVehicleData)}
                        title={`${totalOverrides} custom price${totalOverrides !== 1 ? 's' : ''} - click to manage`}
                    >
                        <i className="fa-sharp fa-regular fa-dollar-sign mr-1"></i>
                        {totalOverrides} CUSTOM PRICE{totalOverrides !== 1 ? 'S' : ''}
                    </button>
                );
            },
            sort: true,
            headerStyle: { width: '180px' },
            style: { textAlign: 'center' }
        }];

        return (
            <React.Fragment>
                {/* Search and Filter Controls */}
                <div className="card mb-4">
                    <div className="card-body">
                        <Row>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Search Vehicles</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search make, model, or trim..."
                                        value={this.state.searchTerm}
                                        onChange={this.handleSearch}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Filter by Make</Form.Label>
                                    <Form.Control
                                        as="select"
                                        value={this.state.selectedMakeFilter}
                                        onChange={this.handleMakeFilter}
                                    >
                                        {makeOptions.map(make => (
                                            <option key={make} value={make}>{make}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={5} className="d-flex align-items-end justify-content-end">
                                {isAdmin && (
                                    <Button
                                        variant="primary"
                                        onClick={this.openAddVehicleModal}
                                        disabled={loading}
                                    >
                                        ADD NEW VEHICLE
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* Vehicle Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-4">
                                <i className="fa-sharp fa-regular fa-spinner fa-spin fa-2x"></i>
                                <p className="mt-2">Loading vehicle catalog...</p>
                            </div>
                        ) : (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">
                                        {groupedVehicles.length} vehicle group{groupedVehicles.length !== 1 ? 's' : ''} found
                                        <small className="text-muted ml-2">
                                            ({this.getFilteredVehicles().length} individual vehicles)
                                        </small>
                                    </h6>
                                </div>
                                
                                <BootstrapTable
                                    keyField='id'
                                    data={groupedVehicles}
                                    columns={columns}
                                    striped
                                    hover
                                    condensed
                                    defaultSorted={[{
                                        dataField: 'make_name',
                                        order: 'asc'
                                    }]}
                                    noDataIndication="No vehicle groups found"
                                />
                            </>
                        )}
                    </div>
                </div>

                {/* Add Vehicle Modal */}
                {this.renderAddVehicleModal()}
                
                {/* Vehicle Edit Modal */}
                {this.renderVehicleEditModal()}
                
                {/* Vehicle-Specific Pricing Modal */}
                {showServiceOverrides && selectedVehicleForOverrides && (
                    <VehicleSpecificPricing
                        show={showServiceOverrides}
                        onHide={this.closeServiceOverridesModal}
                        installerId={this.props.typeId}
                        preSelectedVehicle={selectedVehicleForOverrides}
                        onUpdate={this.handleServiceOverridesUpdated}
                    />
                )}
            </React.Fragment>
        );
    }
}